import { useRef, useState, type DragEvent } from 'react';
import { clsx } from '../../../../ds/clsx';
import { Skeleton } from '../../../../ds/components';
import { useIsMobile } from '../../../../ds/hooks';
import { CroppedAvatar, useAvatarFlow } from '../../../settings/profile/avatarUpload';
import {
  ProfileFieldInput,
  correctionLine,
  useProfileForm,
  type ProfileForm,
} from '../../../settings/profile/ProfileFields';
import { WizardButton } from '../../ui';
import { useProfile } from '../api';
import { ActionError, StepFrame, useWizard } from '../StepFrame';
import type { Profile } from '../types';
import styles from './ProfileStep.module.css';

/** Nutzer 02/03 (M2): picture and fields; „Weiter" saves what changed and completes the step. */
export function ProfileStep() {
  const mobile = useIsMobile();
  const wizard = useWizard();
  const profile = useProfile();
  const form = useProfileForm(profile.data, 'wizard');
  const [saving, setSaving] = useState(false);

  async function next() {
    setSaving(true);
    try {
      const stored = await form.commitAll();
      if (stored) await wizard.act('continue');
    } finally {
      setSaving(false);
    }
  }

  const problems = form.problemCount;
  const note =
    problems > 0 ? (
      <span className={styles.problems}>{correctionLine(problems)}</span>
    ) : (
      'Sie können alles später in den Einstellungen ändern.'
    );
  const primary = {
    label: 'Weiter',
    onClick: () => void next(),
    disabled: problems > 0 || !profile.data,
    loading: saving || wizard.acting === 'continue',
  };
  const skip = {
    label: mobile ? 'Später' : 'Überspringen',
    onClick: () => void wizard.act('skip'),
    loading: wizard.acting === 'skip',
  };

  return (
    <StepFrame
      title="Wie sollen Mandanten Sie sehen?"
      text="Bild und Angaben erscheinen auf Terminbestätigungen, im Mandantenportal und in Ihrer Signatur."
      mobileText="Bild und Angaben erscheinen in Terminbestätigungen und im Portal."
      width={760}
      footer={{ note, secondary: skip, primary }}
    >
      {profile.data ? (
        mobile ? (
          <MobileProfileCard profile={profile.data} form={form} />
        ) : (
          <div className={styles.card}>
            <AvatarPicker profile={profile.data} />
            <div className={styles.grid}>
              <ProfileFieldInput form={form} field="firstName" source="wizard" density="wizard" />
              <ProfileFieldInput form={form} field="lastName" source="wizard" density="wizard" />
              <ProfileFieldInput
                form={form}
                field="jobTitle"
                source="wizard"
                density="wizard"
                placeholder="z. B. Vertrieb Wohnimmobilien"
              />
              <ProfileFieldInput form={form} field="extension" source="wizard" density="wizard" />
              <ProfileFieldInput form={form} field="mobilePhone" source="wizard" density="wizard" />
              <ProfileFieldInput
                form={form}
                field="location"
                source="wizard"
                density="wizard"
                suggestions={profile.data.locationSuggestions}
              />
            </div>
          </div>
        )
      ) : (
        <Skeleton variant="block" height={mobile ? 420 : 262} />
      )}
      <ActionError />
    </StepFrame>
  );
}

/** The 120-px round picker of Nutzer 02/03: „+ Bild wählen", drop zone „↓ Hier ablegen", the refusal below. */
function AvatarPicker({ profile }: { profile: Profile }) {
  const flow = useAvatarFlow();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function onDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setOver(false);
    const file = event.dataTransfer.files[0];
    if (file) flow.choose(file);
  }

  return (
    <div className={styles.picker}>
      <button
        type="button"
        className={clsx(styles.circle, over && styles.circleOver, profile.avatar && styles.circleFilled)}
        aria-label={profile.avatar ? 'Profilbild ersetzen' : 'Profilbild wählen'}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        {over ? (
          <>
            <span className={styles.circleGlyph} aria-hidden="true">
              ↓
            </span>
            <span className={styles.circleDrop}>Hier ablegen</span>
          </>
        ) : profile.avatar ? (
          <CroppedAvatar url={profile.avatar.url} crop={profile.avatar.crop} size={118} alt="" />
        ) : (
          <>
            <span className={styles.circlePlus} aria-hidden="true">
              ＋
            </span>
            <span className={styles.circleText}>Bild wählen</span>
          </>
        )}
      </button>
      {flow.error ? (
        <span className={styles.pickerError} role="alert">
          {flow.error}
        </span>
      ) : (
        <span className={styles.pickerHint}>
          JPG oder PNG
          <br />
          ab 400 × 400 px
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        className={styles.fileInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) flow.choose(file);
        }}
      />
      {flow.dialog}
    </div>
  );
}

/** M2: 96-px picture with „Aufnehmen"/„Galerie" and the list card (Vorname, Nachname, Funktion, Mobil). */
function MobileProfileCard({ profile, form }: { profile: Profile; form: ProfileForm }) {
  const flow = useAvatarFlow();
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const onFile = (file: File | undefined) => {
    if (file) flow.choose(file);
  };
  return (
    <>
      <div className={styles.mobilePicker}>
        {profile.avatar ? (
          <CroppedAvatar url={profile.avatar.url} crop={profile.avatar.crop} size={96} alt="Profilbild" />
        ) : (
          <span className={styles.mobileCircle}>
            <span className={styles.circlePlus} aria-hidden="true">
              ＋
            </span>
            <span className={styles.mobileCircleText}>Bild</span>
          </span>
        )}
        {flow.error ? (
          <span className={styles.pickerError} role="alert">
            {flow.error}
          </span>
        ) : null}
        <div className={styles.mobileButtons}>
          <WizardButton size="mobile" onClick={() => cameraRef.current?.click()}>
            Aufnehmen
          </WizardButton>
          <WizardButton size="mobile" onClick={() => galleryRef.current?.click()}>
            Galerie
          </WizardButton>
        </div>
        <input
          ref={cameraRef}
          type="file"
          accept="image/jpeg,image/png"
          capture="user"
          className={styles.fileInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            onFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/jpeg,image/png"
          className={styles.fileInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            onFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        {flow.dialog}
      </div>
      <div className={styles.mobileList}>
        <ProfileFieldInput form={form} field="firstName" source="wizard" density="mobile" />
        <ProfileFieldInput form={form} field="lastName" source="wizard" density="mobile" />
        <ProfileFieldInput
          form={form}
          field="jobTitle"
          source="wizard"
          density="mobile"
          placeholder="z. B. Vertrieb Wohnimmobilien"
        />
        <ProfileFieldInput form={form} field="mobilePhone" source="wizard" density="mobile" />
      </div>
    </>
  );
}
